import React, { useEffect, useState, useCallback } from "react";
import { Sparkles, X, FileSpreadsheet } from "lucide-react";
import { api } from "../lib/api";

/** Compact relative time — "just now / today / yesterday / DD/MM/YYYY".
 *  Keeps strings terse so the chip stays single-line on mobile. */
function relTime(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diffMs = Date.now() - t;
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  // Normalise both dates to local midnight so "yesterday" is calendar-based,
  // not "exactly 24h ago" — matches how operators actually talk.
  const now = new Date();
  const then = new Date(t);
  const dayStartNow = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayStartThen = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime();
  const dayDelta = Math.round((dayStartNow - dayStartThen) / 86_400_000);
  if (dayDelta === 0) return diffH < 1 ? `${diffMin}m ago` : "today";
  if (dayDelta === 1) return "yesterday";
  // Fallback DD/MM/YYYY — consistent with app-wide date format.
  const dd = String(then.getDate()).padStart(2, "0");
  const mm = String(then.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${then.getFullYear()}`;
}

function formatCell(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return String(v);
  return String(v);
}

/**
 * LastEnrichmentChip — small "last enrichment" strip that appears on
 * project Overview when a `project_enriched_from_excel` audit event
 * occurred within the last 7 days. Clicking opens an in-page slide-over
 * showing the per-field before→after diff for that commit.
 */
export default function LastEnrichmentChip({ projectId }) {
  const [event, setEvent] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/enrich-from-excel/last`);
      setEvent(data?.event || null);
    } catch { setEvent(null); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  // Close slide-over on Esc so keyboard users don't trap themselves.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!event) return null;
  const n = Number(event.count || (event.diff?.length ?? 0));
  if (n <= 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-2 bg-emerald-50 border border-emerald-200 hover:border-emerald-400 hover:bg-emerald-100 transition-colors px-3 py-2 text-left text-xs"
        data-testid="last-enrichment-chip"
        title="View the fields that were filled from spreadsheet"
      >
        <Sparkles size={13} className="text-emerald-700 shrink-0" />
        <span className="text-emerald-900 truncate">
          <strong>{n} field{n === 1 ? "" : "s"}</strong> filled from Excel {relTime(event.created_at)}
          {event.actor ? <> by <strong>{event.actor}</strong></> : null}
        </span>
        <span className="ml-auto text-emerald-800 underline decoration-dotted shrink-0">
          view diff
        </span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex"
          role="dialog"
          aria-modal="true"
          aria-label="Last enrichment diff"
          data-testid="last-enrichment-panel"
        >
          <div
            className="flex-1 bg-black/40"
            onClick={() => setOpen(false)}
            data-testid="last-enrichment-backdrop"
          />
          <aside
            className="w-full sm:w-[520px] bg-white shadow-xl border-l border-[var(--line)] flex flex-col"
            style={{ minWidth: 0 }}
          >
            <header className="px-5 py-4 flex items-center justify-between border-b border-[var(--line)] bg-emerald-50">
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles size={14} className="text-emerald-700" />
                  <div className="font-display text-lg font-bold text-emerald-900">
                    Last enrichment
                  </div>
                </div>
                <div className="text-xs muted mt-1">
                  {n} field{n === 1 ? "" : "s"} filled {relTime(event.created_at)}
                  {event.actor ? <> by <strong>{event.actor}</strong></> : null}
                </div>
                {event.source_filename && (
                  <div className="text-[11px] muted mt-1 flex items-center gap-1">
                    <FileSpreadsheet size={11} /> {event.source_filename}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="btn-ghost"
                data-testid="last-enrichment-close"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto p-4">
              {event.diff && event.diff.length > 0 ? (
                <table className="w-full text-xs" data-testid="last-enrichment-diff-table">
                  <thead className="text-left">
                    <tr className="border-b border-[var(--line)]">
                      <th className="py-2 pr-2">Field</th>
                      <th className="py-2 pr-2">Before</th>
                      <th className="py-2">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {event.diff.map((d) => (
                      <tr
                        key={d.field}
                        className="border-b border-[var(--line)]/60"
                        data-testid={`last-enrichment-row-${d.field}`}
                      >
                        <td className="py-2 pr-2 font-mono text-[11px]">{d.field}</td>
                        <td className="py-2 pr-2 muted">
                          {d.before === null || d.before === undefined || d.before === ""
                            ? <em className="muted">— empty —</em>
                            : formatCell(d.before)}
                        </td>
                        <td className="py-2">
                          <strong>{formatCell(d.after)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-xs muted">
                  Diff details were not recorded for this event.
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
